import { useCallback, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { SubmitErrorAlert } from '@/components/Alerts';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import { useUpdateAccountMutation } from '@/graphql';
import { AccountForm } from '@/routes/Agency/common/components';
import { AccountFormValues } from '@/routes/Agency/common/components/AccountForm';
import { Maybe } from '@/types';
import { GetAccountQuery } from '@/types/graphql';

export type Props = {
  account: GetAccountQuery['account'];
  hideModal: () => void;
};

const UpdateContactModal = ({ account, hideModal }: Props) => {
  const [submitError, setSubmitError] = useState<Maybe<Error>>(null);
  const [updateAccountInformation, { loading }] = useUpdateAccountMutation({
    update: (cache) => {
      cache.modify({
        id: cache.identify(account),
        fields: {
          defaultContact() {},
          addressLine1() {},
          addressLine2() {},
          city() {},
          state() {},
          zip() {},
        },
      });
    },
    onCompleted: hideModal,
    onError: setSubmitError,
  });
  const onSubmit: SubmitHandler<AccountFormValues> = useCallback(
    ({ defaultContact, address }) =>
      updateAccountInformation({
        variables: {
          accountId: account.id,
          defaultContactId: defaultContact.id,
          ...address,
          addressLine2: address.addressLine2 ?? null,
        },
      }),
    []
  );

  return (
    <Modal
      disableClickout
      size="sm"
      title="Update account information"
      onRequestClose={hideModal}
    >
      <Card.Section>
        {submitError && <SubmitErrorAlert description={submitError.message} />}
        <AccountForm
          customer={account.customer}
          isLoading={loading}
          newAccount={false}
          selectedAccount={account}
          onSubmit={onSubmit}
        />
      </Card.Section>
    </Modal>
  );
};

export default UpdateContactModal;
